import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Select, MenuItem, Pagination, Tabs, Tab, FormControl, InputLabel } from '@mui/material';
import CRMTable from '../tables/CRMTable';
import ContactForm from '../dialogs/ContactForm';
import useCRM from '../items/hooks/HCRM';

interface Props {
    user?: any;
}

export default function CRMTab({ user }: Props) {
    const [contactOpen, setContactOpen] = useState(false);
    const [selectedInvestor, setSelectedInvestor] = useState<number | string | null>(null);
    const { fetchInvestors, fetchPeople, listInvestorTypes, listInvestorAUMCurrencies } = useCRM();

    const [activeTab, setActiveTab] = useState(0);

    const [rowsInv, setRowsInv] = useState<any[]>([]);
    const [loadingInv, setLoadingInv] = useState(false);
    const [errorInv, setErrorInv] = useState<string | null>(null);
    const [searchInv, setSearchInv] = useState('');
    const [investorTypes, setInvestorTypes] = useState<string[]>([]);
    const [aumCurrencies, setAumCurrencies] = useState<string[]>([]);
    const [selectedType, setSelectedType] = useState<string>('');
    const [selectedAumCurrency, setSelectedAumCurrency] = useState<string>('');
    const [pageInv, setPageInv] = useState(1);
    const [pageSizeInv, setPageSizeInv] = useState(20);

    const [rowsPeople, setRowsPeople] = useState<any[]>([]);
    const [loadingPeople, setLoadingPeople] = useState(false);
    const [errorPeople, setErrorPeople] = useState<string | null>(null);
    const [searchPeople, setSearchPeople] = useState('');
    const [pagePeople, setPagePeople] = useState(1);
    const [pageSizePeople, setPageSizePeople] = useState(20);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoadingInv(true);
            setErrorInv(null);
            try {
                const res = await fetchInvestors();
                const data = Array.isArray(res) ? res : res?.data ?? [];
                if (mounted) setRowsInv(data);
            } catch (err: any) {
                if (mounted) setErrorInv(err?.message || 'Failed to load investors');
            } finally {
                if (mounted) setLoadingInv(false);
            }
        };
        void load();
        return () => { mounted = false; };
    }, [fetchInvestors]);

    useEffect(() => {
        let mounted = true;
        const loadLists = async () => {
            try {
                const types = listInvestorTypes ? await listInvestorTypes() : [];
                const aums = listInvestorAUMCurrencies ? await listInvestorAUMCurrencies() : [];
                if (!mounted) return;
                setInvestorTypes(Array.isArray(types) ? types : []);
                setAumCurrencies(Array.isArray(aums) ? aums : []);
            } catch (e) {
                console.warn('CRMTab: failed to load investor filter lists', e);
            }
        };
        void loadLists();
        return () => { mounted = false; };
    }, [listInvestorTypes, listInvestorAUMCurrencies]);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoadingPeople(true);
            setErrorPeople(null);
            try {
                const res = await fetchPeople();
                const data = Array.isArray(res) ? res : res?.data ?? [];
                if (mounted) setRowsPeople(data);
            } catch (err: any) {
                if (mounted) setErrorPeople(err?.message || 'Failed to load people');
            } finally {
                if (mounted) setLoadingPeople(false);
            }
        };
        void load();
        return () => { mounted = false; };
    }, [fetchPeople]);

    const handleRowClick = (row: any) => {
        // Robustly extract investor id from various possible column names
        const keys = Object.keys(row || {});
        let found: any = null;

        // Preferred explicit checks first
        if (row && (row.investor_id || row.investorId || row.Investor_id || row.InvestorId)) {
            found = row.investor_id ?? row.investorId ?? row.Investor_id ?? row.InvestorId;
        }

        // Scan keys for names containing both 'investor' and 'id' ignoring case/spacing
        if (found == null) {
            for (const k of keys) {
                const normalized = String(k).toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9_]/g, '');
                if (normalized.includes('investor') && (normalized.includes('id') || normalized.endsWith('_id'))) {
                    found = row[k];
                    break;
                }
            }
        }

        // fallback to common id fields
        if (found == null) {
            found = row.id ?? row.user_id ?? row.company_id ?? null;
        }

        console.log('CRMTab.resolveInvestorId ->', found, 'from row keys:', Object.keys(row || {}));

        setSelectedInvestor(found ?? null);
        setContactOpen(true);
    };

    const filteredInv = useMemo(() => {
        const q = searchInv.trim().toLowerCase();
        // detect keys for type and aum currency
        const keys = Object.keys(rowsInv[0] ?? {});
        const typeKey = keys.find((k) => /investor\s*type/i.test(k) || /type/i.test(k));
        const aumKey = keys.find((k) => /aum.*currency/i.test(k) || /aum_currency/i.test(k) || /investor\s*aum\s*currency/i.test(k)) || keys.find((k) => /currency/i.test(k));

        return rowsInv.filter((r) => {
            try {
                if (q) {
                    const combined = Object.values(r)
                        .map((v) => (v === null || v === undefined ? '' : String(v).toLowerCase()))
                        .join(' ');
                    if (!combined.includes(q)) return false;
                }

                if (selectedType) {
                    const v = typeKey ? r[typeKey] : (r.type ?? r['Investor Type'] ?? '');
                    if (!v || String(v).trim().toLowerCase() !== selectedType.trim().toLowerCase()) return false;
                }

                if (selectedAumCurrency) {
                    const v = aumKey ? r[aumKey] : (r['Investor AUM Currency'] ?? r.aum_currency ?? r['AUM Currency'] ?? r.currency ?? '');
                    if (!v || String(v).trim().toLowerCase() !== selectedAumCurrency.trim().toLowerCase()) return false;
                }

                return true;
            } catch (e) { return false; }
        });
    }, [rowsInv, searchInv, selectedType, selectedAumCurrency]);

    const pageCountInv = Math.max(1, Math.ceil(filteredInv.length / pageSizeInv));
    const currentPageRowsInv = filteredInv.slice((pageInv - 1) * pageSizeInv, pageInv * pageSizeInv);

    useEffect(() => { setPageInv(1); }, [searchInv, pageSizeInv, selectedType, selectedAumCurrency]);

    const filteredPeople = useMemo(() => {
        const q = searchPeople.trim().toLowerCase();
        if (!q) return rowsPeople;
        return rowsPeople.filter((r) => {
            try {
                const combined = Object.values(r)
                    .map((v) => (v === null || v === undefined ? '' : String(v).toLowerCase()))
                    .join(' ');
                return combined.includes(q);
            } catch (e) { return false; }
        });
    }, [rowsPeople, searchPeople]);

    const pageCountPeople = Math.max(1, Math.ceil(filteredPeople.length / pageSizePeople));
    const currentPageRowsPeople = filteredPeople.slice((pagePeople - 1) * pageSizePeople, pagePeople * pageSizePeople);

    useEffect(() => { setPagePeople(1); }, [searchPeople, pageSizePeople]);

    return (
        <div className='standard-div'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div className='standard-div-flex'>
                    <h1>CRM</h1>
                </div>

                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}>
                    <Tab label='Investors' />
                    <Tab label='People' />
                </Tabs>

                {activeTab === 0 && (
                    <>
                        <div className='header-flex'>
                            <TextField
                                size='small'
                                placeholder='Search investors by name, email or company...'
                                value={searchInv}
                                onChange={(e) => setSearchInv(e.target.value)}
                                sx={{ marginRight: 1, minWidth: 300 }}
                            />

                            <Select size='small' value={pageSizeInv} onChange={(e) => setPageSizeInv(Number(e.target.value))} sx={{ minWidth: 96 }}>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                            <FormControl size='small' sx={{ minWidth: 180, marginLeft: 1 }}>
                                <InputLabel id='investor-type-filter-label'>Investor Type</InputLabel>
                                <Select
                                    labelId='investor-type-filter-label'
                                    value={selectedType}
                                    label='Investor Type'
                                    onChange={(e) => setSelectedType(String(e.target.value))}
                                >
                                    <MenuItem value=''>All</MenuItem>
                                    {investorTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl size='small' sx={{ minWidth: 200, marginLeft: 1 }}>
                                <InputLabel id='aum-currency-filter-label'>AUM Currency</InputLabel>
                                <Select
                                    labelId='aum-currency-filter-label'
                                    value={selectedAumCurrency}
                                    label='AUM Currency'
                                    onChange={(e) => setSelectedAumCurrency(String(e.target.value))}
                                >
                                    <MenuItem value=''>All</MenuItem>
                                    {aumCurrencies.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </div>

                        <div style={{ width: '100%' }}>
                            <CRMTable rows={currentPageRowsInv} loading={loadingInv} error={errorInv} onRowClick={handleRowClick} />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                            <Pagination count={pageCountInv} page={pageInv} onChange={(_, v) => setPageInv(v)} color='primary' />
                        </div>
                    </>
                )}

                {activeTab === 1 && (
                    <>
                        <div className='header-flex'>
                            <TextField
                                size='small'
                                placeholder='Search people by name, email or company...'
                                value={searchPeople}
                                onChange={(e) => setSearchPeople(e.target.value)}
                                sx={{ marginRight: 1, minWidth: 300 }}
                            />

                            <Select size='small' value={pageSizePeople} onChange={(e) => setPageSizePeople(Number(e.target.value))} sx={{ minWidth: 96 }}>
                                <MenuItem value={10}>10</MenuItem>
                                <MenuItem value={20}>20</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                            </Select>
                        </div>

                        <div style={{ width: '100%' }}>
                            <CRMTable rows={currentPageRowsPeople} loading={loadingPeople} error={errorPeople} />
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
                            <Pagination count={pageCountPeople} page={pagePeople} onChange={(_, v) => setPagePeople(v)} color='primary' />
                        </div>
                    </>
                )}
            </div>
            <ContactForm open={contactOpen} onClose={() => setContactOpen(false)} investorId={selectedInvestor} />
        </div>
    );
}
